import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { UserRole } from '../apiClients/workspace/types';
import { scheduleApi } from '../features/schedule/api/scheduleApi';
import {
  AddParticipantRequest,
  ConfirmParticipationRequest,
  EventInstance,
  EventInstanceParticipant,
  EventTemplateParticipant,
  GetEventInstancesParams,
  GetEventInstancesResponse,
  ReplaceParticipantRequest,
  ReplaceTemplateParticipantRequest,
  UpdateEventInstanceRequest,
  UpdateParticipantRoleRequest,
} from '../features/schedule/api/types';
import i18n from '../i18n';
import { notificationStore } from './NotificationStore';
import { scheduleStore } from './ScheduleStore';

export class EventInstanceStore {
  @observable instances: EventInstance[] = [];
  @observable isLoading = false;
  @observable error: Error | null = null;
  @observable currentScheduleId: string | null = null;

  constructor() {
    makeObservable(this);
  }

  // ===============================
  // COMPUTED PROPERTIES
  // ===============================

  @computed get isError(): boolean {
    return this.error !== null;
  }

  @computed get hasData(): boolean {
    return this.instances.length > 0;
  }

  @computed get instancesCount(): number {
    return this.instances.length;
  }

  // Get instances grouped by date
  @computed get instancesByDate(): Map<string, EventInstance[]> {
    const grouped = new Map<string, EventInstance[]>();

    this.instances.forEach((instance) => {
      // Extract date part from ISO datetime (e.g., "2025-08-05T00:00:00.000Z" -> "2025-08-05")
      const date = new Date(instance.date).toISOString().split('T')[0];

      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      grouped.get(date)!.push(instance);
    });

    return grouped;
  }

  // ===============================
  // ACTIONS
  // ===============================

  async loadEventInstances(params: GetEventInstancesParams): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const response: GetEventInstancesResponse =
        await scheduleApi.getEventInstances(params);
      runInAction(() => {
        this.instances = response.instances;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      notificationStore.showError(i18n.t('errors.failedToLoadEventInstances'));
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async loadEventInstancesForCurrentSchedule(
    dateFrom?: string,
    dateTo?: string
  ): Promise<void> {
    if (!scheduleStore.hasCurrentSchedule) {
      console.warn('No current schedule available');
      return;
    }

    await this.loadEventInstances({
      dateFrom,
      dateTo,
    });
  }

  async updateEventInstance(
    instanceId: string,
    request: UpdateEventInstanceRequest
  ): Promise<EventInstance | null> {
    try {
      const response = await scheduleApi.updateEventInstance(
        instanceId,
        request
      );

      // Update the instance in our store
      const index = this.instances.findIndex(
        (instance) => instance.id === instanceId
      );
      if (index !== -1) {
        runInAction(() => {
          this.instances[index] = response.instance;
          this.instances = [...this.instances];
        });
      }

      notificationStore.showSuccess(i18n.t('success.eventInstanceUpdated'));
      return response.instance;
    } catch {
      notificationStore.showError(i18n.t('errors.failedToUpdateEventInstance'));
      return null;
    }
  }

  async cancelEvent(instanceId: string): Promise<EventInstance | null> {
    try {
      const response = await scheduleApi.updateEventInstance(instanceId, {
        status: 'Cancelled',
      });

      // Update the instance in our store
      const index = this.instances.findIndex(
        (instance) => instance.id === instanceId
      );
      if (index !== -1) {
        runInAction(() => {
          this.instances[index] = response.instance;
          this.instances = [...this.instances];
        });
      }

      notificationStore.showSuccess(i18n.t('success.eventCancelled'));
      return response.instance;
    } catch {
      notificationStore.showError(i18n.t('errors.failedToCancelEvent'));
      return null;
    }
  }

  async confirmEvent(instanceId: string): Promise<EventInstance | null> {
    try {
      const response = await scheduleApi.updateEventInstance(instanceId, {
        status: 'Active',
      });

      // Update the instance in our store
      const index = this.instances.findIndex(
        (instance) => instance.id === instanceId
      );
      if (index !== -1) {
        runInAction(() => {
          this.instances[index] = response.instance;
          this.instances = [...this.instances];
        });
      }

      notificationStore.showSuccess(i18n.t('success.eventConfirmed'));
      return response.instance;
    } catch {
      notificationStore.showError(i18n.t('errors.failedToConfirmEvent'));
      return null;
    }
  }

  @action async confirmParticipation(
    instanceId: string,
    userId: string,
    request: ConfirmParticipationRequest
  ): Promise<EventInstanceParticipant | null> {
    try {
      const participant = await scheduleApi.confirmParticipation(
        instanceId,
        userId,
        request
      );

      // Update participants in our store
      this.updateParticipantInStore(instanceId, participant);

      notificationStore.showSuccess(i18n.t('success.participationConfirmed'));
      return participant;
    } catch {
      notificationStore.showError(
        i18n.t('errors.failedToConfirmParticipation')
      );
      return null;
    }
  }

  async cancelParticipation(
    instanceId: string,
    userId: string,
    { actor, withValidExcuse }: { actor: UserRole; withValidExcuse?: boolean }
  ): Promise<EventInstanceParticipant | null> {
    try {
      const participant = await scheduleApi.cancelParticipation(
        instanceId,
        userId,
        { actor, withValidExcuse }
      );

      // Update participants in our store
      this.updateParticipantInStore(instanceId, participant);

      notificationStore.showSuccess(i18n.t('success.participationCancelled'));
      return participant;
    } catch {
      notificationStore.showError(i18n.t('errors.failedToCancelParticipation'));
      return null;
    }
  }

  @action async replaceParticipant(
    instanceId: string,
    userId: string,
    request: ReplaceParticipantRequest
  ): Promise<EventInstanceParticipant | null> {
    try {
      const participant = await scheduleApi.replaceParticipant(
        instanceId,
        userId,
        request
      );

      // Update participants in our store
      this.updateParticipantInStore(instanceId, participant);

      notificationStore.showSuccess(i18n.t('success.participantReplaced'));
      return participant;
    } catch {
      notificationStore.showError(i18n.t('errors.failedToReplaceParticipant'));
      return null;
    }
  }

  @action async updateParticipantRole(
    instanceId: string,
    userId: string,
    request: UpdateParticipantRoleRequest
  ): Promise<EventInstanceParticipant | null> {
    try {
      const participant = await scheduleApi.updateParticipantRole(
        instanceId,
        userId,
        request
      );

      // Update participants in our store
      this.updateParticipantInStore(instanceId, participant);

      notificationStore.showSuccess(i18n.t('success.participantRoleUpdated'));
      return participant;
    } catch {
      notificationStore.showError(
        i18n.t('errors.failedToUpdateParticipantRole')
      );
      return null;
    }
  }

  @action async addParticipant(
    instanceId: string,
    userId: string,
    request: AddParticipantRequest
  ): Promise<EventInstanceParticipant | null> {
    try {
      const participant = await scheduleApi.addParticipant(
        instanceId,
        userId,
        request
      );

      // Update participants in our store
      this.updateParticipantInStore(instanceId, participant);

      notificationStore.showSuccess(i18n.t('success.participantAdded'));
      return participant;
    } catch {
      notificationStore.showError(i18n.t('errors.failedToAddParticipant'));
      return null;
    }
  }

  @action async deleteParticipant(
    instanceId: string,
    userId: string
  ): Promise<EventInstanceParticipant | null> {
    try {
      const participant = await scheduleApi.deleteParticipant(
        instanceId,
        userId
      );

      // Remove participant from store
      const instance = this.instances.find((i) => i.id === instanceId);
      if (instance) {
        instance.participants = instance.participants.filter(
          (p) => p.userId !== userId
        );
        this.instances = [...this.instances]; // To rerender
      }

      notificationStore.showSuccess(i18n.t('success.participantDeleted'));
      return participant;
    } catch {
      notificationStore.showError(i18n.t('errors.failedToDeleteParticipant'));
      return null;
    }
  }

  // ===============================
  // TEMPLATE PARTICIPANT ACTIONS
  // ===============================

  /**
   * Add participant to a template (affects future instances)
   */
  @action async addTemplateParticipant(
    templateId: string,
    participantId: string
  ): Promise<EventTemplateParticipant | null> {
    try {
      const response = await scheduleApi.addTemplateParticipant(templateId, {
        participantId,
      });

      // Reload instances and templates to reflect changes
      await this.reloadAfterTemplateChange(templateId);

      notificationStore.showSuccess(i18n.t('success.templateParticipantAdded'));
      return response.participant;
    } catch {
      notificationStore.showError(
        i18n.t('errors.failedToAddTemplateParticipant')
      );
      return null;
    }
  }

  /**
   * Delete participant from a template (affects future instances)
   */
  @action async deleteTemplateParticipant(
    templateId: string,
    participantId: string
  ): Promise<boolean> {
    try {
      await scheduleApi.deleteTemplateParticipant(templateId, participantId);

      // Reload instances and templates to reflect changes
      await this.reloadAfterTemplateChange(templateId);

      notificationStore.showSuccess(
        i18n.t('success.templateParticipantDeleted')
      );
      return true;
    } catch {
      notificationStore.showError(
        i18n.t('errors.failedToDeleteTemplateParticipant')
      );
      return false;
    }
  }

  /**
   * Replace participant in a template (affects future instances)
   */
  @action async replaceTemplateParticipant(
    templateId: string,
    participantId: string,
    replacingParticipantId: string
  ): Promise<EventTemplateParticipant | null> {
    try {
      const request: ReplaceTemplateParticipantRequest = {
        replacingParticipantId,
      };
      const response = await scheduleApi.replaceTemplateParticipant(
        templateId,
        participantId,
        request
      );

      // Reload instances and templates to reflect changes
      await this.reloadAfterTemplateChange(templateId);

      notificationStore.showSuccess(
        i18n.t('success.templateParticipantReplaced')
      );
      return response.replacedParticipant;
    } catch {
      notificationStore.showError(
        i18n.t('errors.failedToReplaceTemplateParticipant')
      );
      return null;
    }
  }

  /**
   * Reload instances and templates after template change
   */
  private async reloadAfterTemplateChange(templateId: string): Promise<void> {
    // Reload templates to get updated participant list
    await scheduleStore.loadTemplates();

    // Reload instances that are based on this template
    const templateInstances = this.instances.filter(
      (i) => i.templateId === templateId
    );
    if (templateInstances.length > 0) {
      await this.loadEventInstancesForCurrentSchedule();
    }
  }

  @action clear(): void {
    this.instances = [];
    this.isLoading = false;
    this.error = null;
    this.currentScheduleId = null;
  }

  // ===============================
  // PRIVATE METHODS
  // ===============================

  @action private updateParticipantInStore(
    instanceId: string,
    updatedParticipant: EventInstanceParticipant
  ): void {
    const currentParticipants =
      this.instances.find((instance) => instance.id === instanceId)
        ?.participants || [];
    const index = currentParticipants.findIndex(
      (p) => p.userId === updatedParticipant.userId
    );

    if (index !== -1) {
      currentParticipants[index] = updatedParticipant;
    } else {
      currentParticipants.push(updatedParticipant);
    }

    this.instances = [...this.instances]; // To rerender
  }

  // ===============================
  // LIFECYCLE METHODS
  // ===============================

  async initialize(): Promise<void> {
    // Wait for scheduleStore to be initialized and then load instances for current schedule
    if (scheduleStore.hasCurrentSchedule) {
      this.loadEventInstancesForCurrentSchedule();
    }
  }

  destroy(): void {
    this.clear();
  }
}

// Export singleton instance
export const eventInstanceStore = new EventInstanceStore();
